/**
 * Analytics service for tracking events and page views
 */

// Determine environment based on hostname
export const determineEnvironment = () => {
  const hostname = window.location.hostname;
  // console.log("Analytics: Current hostname is", hostname);

  if (hostname.includes('test.southvanmed.com')) {
    console.log('Analytics: Environment detected as TEST');
    return 'test';
  }

  if (hostname === 'localhost' || hostname.includes('127.0.0.1')) {
    console.log('Analytics: Environment detected as DEVELOPMENT');
    return 'development';
  }

  console.log('Analytics: Environment detected as PRODUCTION');
  return 'production';
};

// Track page views
export const trackPageView = (path, authenticated) => {
  window.dataLayer = window.dataLayer || [];

  const environment = determineEnvironment();
  const pageViewData = {
    event: 'page_view',
    environment: environment,
    page_path: path,
    user_authenticated: authenticated || false
  };

  // console.log("Analytics: Tracking page view", pageViewData);
  window.dataLayer.push(pageViewData);

  // Log the current dataLayer state after pushing
  // console.log("Analytics: Current dataLayer state:", [...window.dataLayer]);
};

// Track user events
export const trackEvent = (eventName, properties = {}) => {
  window.dataLayer = window.dataLayer || [];

  const environment = determineEnvironment();
  const eventData = {
    event: eventName,
    environment: environment,
    ...properties
  };

  console.log(`🔥 Analytics: Tracking event "${eventName}"`, eventData);
  window.dataLayer.push(eventData);

  // Log the current dataLayer state after pushing
  console.log('🔥 Analytics: Current dataLayer state:', [...window.dataLayer]);
};

// Debug helper function to check if GTM is properly loaded
export const debugGTM = () => {
  if (window.google_tag_manager && window.google_tag_manager['GTM-PCZKD5DL']) {
    // console.log("Analytics: GTM is properly loaded!");
    return true;
  } else {
    console.warn("Analytics: GTM is not loaded or container ID doesn't match!");
    return false;
  }
};

// Initialize analytics
export const initAnalytics = () => {
  // console.log("Analytics: Initializing analytics service");

  // Check if we already have a dataLayer
  if (window.dataLayer && window.dataLayer.length) {
    // console.log("Analytics: DataLayer already exists with", window.dataLayer.length, "items");
  } else {
    // console.log("Analytics: Creating new dataLayer");
    window.dataLayer = [];
  }

  // Set environment in dataLayer
  const environment = determineEnvironment();
  window.dataLayer.push({
    event: 'analytics_initialized',
    environment: environment
  });

  // Check if GTM is loaded after a short delay
  setTimeout(debugGTM, 1000);

  return environment;
};

// CBC Campaign Specific Helper Functions

/**
 * Track CBC campaign events with automatic attribution detection
 */
export const trackCBCEvent = (eventName, additionalProperties = {}) => {
  try {
    // Check if user has CBC attribution (with 30-day expiration)
    const cbcAttribution = localStorage.getItem('cbc_campaign_attribution') || sessionStorage.getItem('cbc_campaign_attribution');

    if (!cbcAttribution) {
      // Not a CBC user, skip tracking
      return false;
    }

    const attribution = JSON.parse(cbcAttribution);

    // Check if attribution has expired (30 days)
    if (attribution.expires && new Date() > new Date(attribution.expires)) {
      localStorage.removeItem('cbc_campaign_attribution');
      return false;
    }

    // Track with CBC campaign data
    const eventData = {
      campaign_source: 'cbc',
      campaign_name: attribution.campaign || 'default',
      campaign_medium: attribution.medium || 'display',
      campaign_content: attribution.content || '',
      ...additionalProperties
    };

    console.log(`🔥 CBC Event Data Being Sent:`, eventData);

    // Send to GTM via dataLayer (includes environment automatically)
    trackEvent(eventName, eventData);

    console.log(`🔥 CBC Event Tracked: ${eventName}`);
    return true;
  } catch (error) {
    console.error('Error tracking CBC event:', error);
    return false;
  }
};

/**
 * Check if current user is from CBC campaign
 */
export const isCBCUser = () => {
  try {
    const cbcAttribution = sessionStorage.getItem('cbc_campaign_attribution');
    return !!cbcAttribution;
  } catch (error) {
    return false;
  }
};

/**
 * Get CBC campaign attribution data
 */
export const getCBCAttribution = () => {
  try {
    const cbcAttribution = sessionStorage.getItem('cbc_campaign_attribution');
    return cbcAttribution ? JSON.parse(cbcAttribution) : null;
  } catch (error) {
    console.error('Error getting CBC attribution:', error);
    return null;
  }
};

/**
 * Track CBC conversion funnel steps with enhanced form type tracking
 */
export const trackCBCFunnelStep = (step, formType = '', additionalData = {}) => {
  const stepEvents = {
    impression: 'cbc_impression',
    landing: 'cbc_landing',
    form_selected: 'cbc_form_selected',
    form_completed: 'cbc_form_completed',
    payment_started: 'cbc_payment_started',
    payment_success: 'cbc_payment_success'
  };

  const eventName = stepEvents[step];
  if (!eventName) {
    console.warn(`Unknown CBC funnel step: ${step}`);
    return false;
  }

  // Enhanced tracking data with form type details
  const trackingData = {
    funnel_step: step,
    form_type: formType, // 'sick-note' or 'school-work-form'
    step_timestamp: new Date().toISOString(),
    ...additionalData
  };

  // Add form-specific data
  if (formType === 'school-work-form') {
    trackingData.form_category = 'school_work';
    trackingData.is_complex_form = true;
  } else if (formType === 'sick-note') {
    trackingData.form_category = 'sick_note';
    trackingData.is_complex_form = false;
  }

  return trackCBCEvent(eventName, trackingData);
};

/**
 * 🧪 DEBUG: Send events directly to GA4 (bypass GTM)
 * Use this to test if custom parameters work when sent directly to GA4
 */
export const testDirectGA4Event = (eventName, customParams = {}) => {
  // Only works if gtag is available (loaded via GA4 directly, not just GTM)
  if (typeof window.gtag !== 'undefined') {
    console.log('🧪 DIRECT GA4 TEST: Sending event directly to GA4');
    console.log('Event Name:', eventName);
    console.log('Custom Params:', customParams);

    window.gtag('event', eventName, {
      ...customParams,
      debug_mode: true // This helps in GA4 DebugView
    });

    console.log('✅ Direct GA4 event sent');
    return true;
  } else {
    console.log('❌ gtag not available - cannot send direct GA4 event');
    return false;
  }
};

/**
 * 🧪 DEBUG: Test CBC payment started with direct GA4
 */
export const testCBCPaymentStartedDirect = formType => {
  return testDirectGA4Event('cbc_payment_started_direct', {
    form_type: formType,
    form_category: formType === 'school-work-form' ? 'school_work' : 'sick_note',
    is_complex_form: formType === 'school-work-form',
    test_mode: true,
    timestamp: new Date().toISOString()
  });
};

/**
 * 🧪 DEBUG: Test CBC payment success manually
 */
export const testCBCPaymentSuccess = (formType = 'sick-note') => {
  console.log('🧪 MANUAL TEST: Triggering cbc_payment_success');

  // Set temporary CBC attribution for testing
  const testAttribution = {
    source: 'cbc',
    campaign: 'test',
    medium: 'display',
    expires: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString()
  };
  localStorage.setItem('cbc_campaign_attribution', JSON.stringify(testAttribution));

  // Trigger the event
  return trackCBCEvent('cbc_payment_success', {
    form_type: formType,
    form_category: formType === 'school-work-form' ? 'school_work' : 'sick_note',
    funnel_step: 'payment_success',
    is_complex_form: formType === 'school-work-form',
    payment_success: true,
    test_mode: true
  });
};

/**
 * 🔧 DEBUG: Check CBC Attribution Status
 */
export const debugCBCAttribution = () => {
  const localStorage_cbc = localStorage.getItem('cbc_campaign_attribution');
  const sessionStorage_cbc = sessionStorage.getItem('cbc_campaign_attribution');

  console.log('🔍 CBC Attribution Debug:');
  console.log('  localStorage:', localStorage_cbc);
  console.log('  sessionStorage:', sessionStorage_cbc);

  if (localStorage_cbc || sessionStorage_cbc) {
    console.log('✅ CBC Attribution EXISTS');
    const attribution = JSON.parse(localStorage_cbc || sessionStorage_cbc);
    console.log('  Parsed:', attribution);

    // Check expiration
    if (attribution.expires && new Date() > new Date(attribution.expires)) {
      console.log('❌ CBC Attribution EXPIRED');
    } else {
      console.log('✅ CBC Attribution VALID');
    }
  } else {
    console.log('❌ NO CBC Attribution found');
  }
};

// 🧪 DEBUG: Make functions available globally for console testing
if (typeof window !== 'undefined') {
  window.debugCBC = debugCBCAttribution;
  window.testCBCPaymentSuccess = testCBCPaymentSuccess;
  window.testCBCFunnelStep = trackCBCFunnelStep;
}
